import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TreeStructure } from "@phosphor-icons/react";

export default function Login() {
  const { login, formatApiError: fmt } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextParam = new URLSearchParams(location.search).get("next");
  const nextPath = nextParam && nextParam.startsWith("/") ? nextParam : "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(nextPath);
    } catch (err) {
      toast.error(fmt(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory grain-bg flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-10" data-testid="login-logo-link">
          <span className="w-10 h-10 rounded-full bg-forest text-gold flex items-center justify-center">
            <TreeStructure size={22} weight="duotone" />
          </span>
          <span className="font-display text-2xl text-forest tracking-tight">LegacyLink</span>
        </Link>
        <div className="legacy-card p-8 md:p-10">
          <h1 className="font-display text-3xl text-forest mb-1">Welcome back</h1>
          <p className="text-sageGray text-sm mb-8">Sign in to your family circle.</p>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-forest">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required
                className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-forest">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required
                className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="login-password-input" />
            </div>
            <Button disabled={busy} type="submit" className="w-full bg-forest hover:bg-forestSoft text-ivory h-11 rounded-full" data-testid="login-submit-button">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="text-sm text-sageGray text-center mt-6">
            New to LegacyLink? <Link to={`/register${nextParam ? `?next=${encodeURIComponent(nextParam)}` : ""}`} className="text-forest underline decoration-gold underline-offset-4" data-testid="login-to-register-link">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
